import { createSelector } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import { PRODUCT_NAME_MAP } from 'common/billing/constants'
import { ManychatLegalEntity } from 'common/billing/constants/AccountBillingInfo'
import { BillingCurrency } from 'common/billing/constants/BillingCurrency'
import { AddOnStatus } from 'common/billing/entities/addOns/interfaces'
import { getAddOnsData } from 'common/billing/entities/addOns/selectors'
import { applyCouponForProduct } from 'common/billing/entities/cart/helpers'
import { CartProductAddOn, CartProductPlan } from 'common/billing/entities/cart/interfaces'
import { getBillingProInfo } from 'common/billing/entities/pro/selectors'
import { ProProduct } from 'common/core/interfaces/products'
import { getAccountFlags, getCurrentAccount } from 'common/core/selectors/appSelectors'
import { PriceData } from 'shared/api/requests/billing/schemas'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'

/**
 * Returns amount and currency for price data:
 * Price for inbox additional seat
 * Price for e-mail channel for 1 sent e-mail
 * Price for WhatsApp paid conversations
 * Price for 1 sms sent, SMS channel
 */
const defaultPriceData: PriceData = {
  seat: { amount: 0, currency: BillingCurrency.USD },
  email: { amount: 0, currency: BillingCurrency.USD },
  sms: { amount: 0, currency: BillingCurrency.USD },
  wa: { amount: 0, currency: BillingCurrency.USD },
}

export const getPriceData = createSelector(
  [getBillingProInfo],
  ({ price_data }): PriceData => price_data ?? defaultPriceData,
)

/**
 * This selector retrieves the billing currency of the account.
 */
export const getBillingCurrency = (state: Pick<RootState, 'app'>): BillingCurrency =>
  state.app.currentAccount.billing_info?.currency ?? BillingCurrency.USD

/**
 * Selector to get the localized total price for active products and addOns.
 * Used in the billing payment details.
 */
export const getLocalizedTotalPaymentDetailsPrice = createSelector(
  [getBillingProInfo, getAddOnsData, getBillingCurrency],
  (pro, addOnsData, currency) => {
    const products = Object.fromEntries(
      Object.entries(pro.products || {}).filter(([, product]) => product.active),
    )
    const addOns = addOnsData.filter((addOn) => addOn.status === AddOnStatus.ACTIVE)
    const totalPaymentDetailsPrice =
      Object.values(products).reduce((total, product) => total + product.price, 0) +
      addOns.reduce((total, addOn) => total + addOn.price, 0)
    return l.currency(totalPaymentDetailsPrice ?? 0, currency, 0)
  },
)

/**
 * Selector to get the localized total price for active products and addOns.
 * Used in the billing payment details.
 */
export const getLocalizedTotalPaymentDetailsPriceDiscounted = createSelector(
  [getBillingProInfo, getAddOnsData, getBillingCurrency],
  (pro, addOnsData, currency) => {
    const appliedCoupon = pro.coupon_info
    if (!appliedCoupon) {
      return null
    }

    const activePlans = Object.entries(pro.products || {}).filter(([, product]) => product.active)
    const plans: CartProductPlan[] = activePlans.map(([slug, plan]) => ({
      type: 'plan',
      price: plan.price,
      slug: slug as ProProduct,
      name: PRODUCT_NAME_MAP[slug as ProProduct],
    }))
    const addOns: CartProductAddOn[] = addOnsData
      .filter((addOn) => addOn.status === AddOnStatus.ACTIVE)
      .map((addOn) => ({
        type: 'addOn',
        price: addOn.price,
        slug: addOn.slug,
        name: addOn.name,
      }))

    const totalDiscountedAmount = [...plans, ...addOns]
      .map((product) => applyCouponForProduct(product, appliedCoupon))
      .map((product) => product.discount?.price ?? product.price)
      .reduce((total, productPrice) => total + productPrice, 0)

    const precision = Number.isInteger(totalDiscountedAmount) ? 0 : 2

    return l.currency(totalDiscountedAmount ?? 0, currency, precision)
  },
)

export const getManychatLegalEntity = createSelector(
  [getCurrentAccount],
  (account) => account.billing_info?.legal_entity,
)

export const getIsBrazilBilling = createSelector(
  getManychatLegalEntity,
  (legalEntity) => legalEntity === ManychatLegalEntity.Brazil,
)

export const getIsBrazilInAppCardAttachmentEnabled = createSelector(
  getIsBrazilBilling,
  getAccountFlags,
  (isBrazilBilling, flags): boolean =>
    Boolean(isBrazilBilling && flags[AccountFlag.ENABLE_IN_APP_CHECKOUT_BRAZIL]),
)
